import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type InputHTMLAttributes, type KeyboardEvent } from 'react';
import { useDebounce } from '../hooks/useDebounce';
import { extractErrorMessage } from '../api/client';
import { posicionDropdown } from '../lib/itemsOC';
import { listaDeProveedores, textoArticulo, useIdentidades } from '../lib/identidadArticulos';
import { ProveedorElegido } from './CodigoArticulo';

export { listaDeProveedores };

/** What the list shows: SKU, description and whether it is active; the rest when the search returns it. */
export interface SkuSugerido {
  skuInterno: string;
  descripcion: string | null;
  /** false = discontinuado: se muestra con la marca "inactivo". */
  activo?: boolean;
  /** Code users see (falls back to the SKU). */
  codigo?: string | null;
  /** The article's identity supplier (supplier + code). */
  proveedor?: { id: number; nombre: string } | null;
  /** The article's code in the document supplier's list. */
  codigoProveedor?: string | null;
  /** Price shown in the option (purchase screens); only when > 0. */
  precioReferencia?: number | null;
}

/** Con menos caracteres no se busca (el catálogo es grande). */
export const MIN_CARACTERES_SKU = 2;

type Estado = 'inactivo' | 'buscando' | 'listo' | 'error';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'onBlur' | 'onKeyDown' | 'role'> & {
  value: string;
  /** Lo que se escribe (texto libre: también se puede cargar un SKU sin elegirlo de la lista). */
  onChange: (texto: string) => void;
  /** A SKU picked from the list (mouse or Enter) or resolved by `resolver`. */
  onElegir: (sku: SkuSugerido) => void;
  onBlur?: () => void;
  /** Busca por texto; tiene que ser estable (una función de módulo o memorizada). */
  buscar: (texto: string) => Promise<SkuSugerido[]>;
  /**
   * Looks up the exact typed code on blur, or on Enter with no option highlighted, when the text changed since the last
   * pick or resolve (the value at mount counts as resolved). One result is picked; several show the ambiguity notice.
   */
  resolver?: (codigo: string) => Promise<SkuSugerido[]>;
  /** Fixed-position list, for fields inside modals or scrolling tables that would clip it. */
  flotante?: boolean;
  /**
   * `value` is a stored SKU: once committed (picked, or set by the page: resolved or loaded) the field shows its code;
   * while the user types it shows exactly what was typed and looks nothing up.
   */
  mostrarCodigo?: boolean;
  /** With mostrarCodigo: «Proveedor: …» under a committed field. */
  mostrarProveedor?: boolean;
  demoraMs?: number;
  'data-falta'?: string;
};

/** Option text: code · description · supplier, then the supplier code when it differs. */
function partesOpcion(s: SkuSugerido) {
  const codigo = s.codigo || s.skuInterno;
  const resto = [s.descripcion, s.proveedor?.nombre].filter(Boolean).map(p => ` · ${p}`).join('')
    + (s.codigoProveedor && s.codigoProveedor !== codigo ? ` · Cód. prov. ${s.codigoProveedor}` : '');
  return { codigo, resto };
}

/** Purchase-screen checkbox that lifts the document-supplier filter. */
export function OtrosProveedores({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-slate-700">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      Buscar artículos de otros proveedores
    </label>
  );
}

/** Note under a line whose article belongs to a supplier other than the document's (nothing without a document supplier). */
export function ProveedorDelArticulo({ proveedor, proveedorDocumentoId }: {
  proveedor?: { id: number; nombre: string } | null; proveedorDocumentoId?: number | string;
}) {
  if (!proveedor || proveedorDocumentoId == null || proveedorDocumentoId === '' || String(proveedor.id) === String(proveedorDocumentoId)) return null;
  return <p className="mt-0.5 text-xs text-amber-700">Artículo de {proveedor.nombre}</p>;
}

/**
 * Campo de SKU con sugerencias (combobox): se escribe libremente y, desde 2 caracteres, muestra los SKU que coinciden.
 * Flechas para moverse, Enter elige, Escape cierra (sin cerrar la ventana de abajo); clic afuera también cierra.
 */
export function BuscadorSku({
  value, onChange, onElegir, onBlur, buscar, resolver, flotante = false, demoraMs = 250, className, id,
  mostrarCodigo = false, mostrarProveedor = false, ...resto
}: Props) {
  const idPropio = useId();
  const idCampo = id ?? `sku-${idPropio}`;
  const idLista = `${idCampo}-lista`;
  const raiz = useRef<HTMLDivElement>(null);
  const campo = useRef<HTMLInputElement>(null);
  const [abierto, setAbierto] = useState(false);
  const [resultados, setResultados] = useState<SkuSugerido[]>([]);
  const [estado, setEstado] = useState<Estado>('inactivo');
  const [error, setError] = useState('');
  const [activo, setActivo] = useState(-1);
  const [ambiguo, setAmbiguo] = useState('');
  const [estiloFlotante, setEstiloFlotante] = useState<CSSProperties>({});
  const texto = useDebounce(value.trim(), demoraMs);
  // Texts already picked or resolved (not resolved again); the current value, to drop stale answers.
  const resueltos = useRef(new Set([value.trim()]));
  const valorActual = useRef(value);
  valorActual.current = value;
  // A value the page sets (not typed here: a Cod.Prov pick filled the SKU) counts as resolved, so blur/Enter keep it.
  const tipeado = useRef<string | null>(null);
  useEffect(() => {
    if (value !== tipeado.current) resueltos.current = new Set([value.trim()]);
  }, [value]);
  // Typed text is in progress (shown raw, not looked up) until the field is left; a value the page set, a pick or a
  // blur commits it (shown as its code, with its supplier). A page that resolves on blur to the same SKU (codigo ==
  // sku_interno) changes nothing, so leaving the field is what commits it.
  const enEdicion = tipeado.current !== null && value === tipeado.current;
  const [, alSalir] = useState(0);
  const identidad = useIdentidades(mostrarCodigo && !enEdicion ? [value] : []);
  const comprometida = mostrarCodigo && !enEdicion ? identidad(value) : undefined;
  // The resolver answers after the render that asked: it calls the latest handler, which sees the latest form state.
  const alElegir = useRef(onElegir);
  alElegir.current = onElegir;

  // Busca el texto (ya sin tipear) mientras la lista está abierta; una respuesta de un texto anterior se descarta.
  useEffect(() => {
    if (!abierto || texto.length < MIN_CARACTERES_SKU) {
      setResultados([]); setEstado('inactivo'); setActivo(-1);
      return;
    }
    let vigente = true;
    setEstado('buscando');
    buscar(texto).then(
      r => { if (vigente) { setResultados(Array.isArray(r) ? r : []); setActivo(-1); setEstado('listo'); } },
      e => { if (vigente) { setResultados([]); setActivo(-1); setError(extractErrorMessage(e)); setEstado('error'); } },
    );
    return () => { vigente = false; };
  }, [texto, abierto, buscar]);

  useEffect(() => {
    if (!abierto) return;
    const alClic = (e: MouseEvent) => { if (!raiz.current?.contains(e.target as Node)) setAbierto(false); };
    document.addEventListener('mousedown', alClic);
    return () => document.removeEventListener('mousedown', alClic);
  }, [abierto]);

  const idOpcion = (i: number) => `${idLista}-${i}`;
  useEffect(() => {
    if (activo >= 0) document.getElementById(idOpcion(activo))?.scrollIntoView?.({ block: 'nearest' });
  });

  const visible = abierto && value.trim().length >= MIN_CARACTERES_SKU && estado !== 'inactivo';
  const conOpciones = visible && estado === 'listo' && resultados.length > 0;

  // Floating: the list is placed next to the field when it opens and follows it on any scroll (capture) or resize.
  useLayoutEffect(() => {
    if (!flotante || !visible) return;
    const ubicar = () => {
      const el = campo.current;
      if (el?.isConnected) setEstiloFlotante(posicionDropdown(el.getBoundingClientRect(), { ancho: window.innerWidth, alto: window.innerHeight }));
    };
    ubicar();
    window.addEventListener('scroll', ubicar, true);
    window.addEventListener('resize', ubicar);
    return () => { window.removeEventListener('scroll', ubicar, true); window.removeEventListener('resize', ubicar); };
  }, [flotante, visible]);

  const elegir = (s: SkuSugerido) => {
    resueltos.current = new Set([s.skuInterno, s.codigo || s.skuInterno]);
    tipeado.current = null;
    setAmbiguo('');
    alElegir.current(s);
    setAbierto(false);
    setActivo(-1);
  };

  /** Resolves the typed code (if it changed): one is picked, several are flagged, none does nothing. */
  const resolverTexto = () => {
    const codigo = value.trim();
    if (!resolver || codigo.length < MIN_CARACTERES_SKU || resueltos.current.has(codigo)) return false;
    resueltos.current = new Set([codigo]);
    resolver(codigo).then(r => {
      if (valorActual.current.trim() !== codigo || !Array.isArray(r)) return;
      if (r.length === 1) elegir(r[0]);
      else if (r.length > 1) {
        setAmbiguo(listaDeProveedores([...new Set(r.map(x => x.proveedor?.nombre ?? 'sin proveedor'))]));
      }
    }, () => { resueltos.current = new Set(); });
    return true;
  };

  const alTeclear = (e: KeyboardEvent<HTMLInputElement>) => {
    const n = resultados.length;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!abierto) { setAbierto(true); return; }
        if (conOpciones) setActivo(i => (i + 1) % n);
        return;
      case 'ArrowUp':
        e.preventDefault();
        if (conOpciones) setActivo(i => (i <= 0 ? n - 1 : i - 1));
        return;
      case 'Enter':
        if (conOpciones && activo >= 0) { e.preventDefault(); elegir(resultados[activo]); }
        else if (resolverTexto()) { e.preventDefault(); setAbierto(false); }
        return;
      case 'Escape':
        if (abierto) { e.preventDefault(); e.stopPropagation(); setAbierto(false); setActivo(-1); }
        return;
    }
  };

  const claseLista = `${flotante ? '' : 'absolute z-30 left-0 right-0 mt-1 max-h-60 '}bg-white border border-slate-200 rounded-lg shadow-lg overflow-auto text-sm`;
  const estiloLista = flotante ? estiloFlotante : undefined;
  const idAviso = `${idCampo}-ambiguo`;

  return (
    <div ref={raiz} className="relative">
      <input
        {...resto}
        ref={campo}
        id={idCampo}
        role="combobox"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={visible}
        aria-controls={conOpciones ? idLista : undefined}
        aria-activedescendant={conOpciones && activo >= 0 ? idOpcion(activo) : undefined}
        aria-invalid={ambiguo ? true : resto['aria-invalid']}
        aria-describedby={ambiguo ? idAviso : resto['aria-describedby']}
        value={textoArticulo(value, comprometida)}
        onChange={e => { tipeado.current = e.target.value; onChange(e.target.value); setAbierto(true); setActivo(-1); setAmbiguo(''); }}
        onKeyDown={alTeclear}
        onBlur={() => { tipeado.current = null; alSalir(n => n + 1); setAbierto(false); resolverTexto(); onBlur?.(); }}
        className={className ?? 'w-full px-3 py-2 border rounded-lg'}
      />
      {visible && !conOpciones && (
        <div className={claseLista} style={estiloLista}>
          {estado === 'buscando' && <p className="px-3 py-2 text-slate-500">Buscando…</p>}
          {estado === 'error' && (
            <p role="alert" className="px-3 py-2 text-red-600">No se pudo buscar el SKU: {error}</p>
          )}
          {estado === 'listo' && <p className="px-3 py-2 text-slate-500">Sin resultados</p>}
        </div>
      )}
      {conOpciones && (
        <ul id={idLista} role="listbox" aria-label="SKU sugeridos" className={claseLista} style={estiloLista}>
          {resultados.map((s, i) => {
            const { codigo, resto: detalle } = partesOpcion(s);
            const precio = Number(s.precioReferencia ?? 0);
            return (
              <li
                key={`${i}-${s.skuInterno}`}
                id={idOpcion(i)}
                role="option"
                aria-selected={i === activo}
                // Sin perder el foco del campo: elegir no cuenta como salir de él.
                onMouseDown={e => e.preventDefault()}
                onClick={() => elegir(s)}
                onMouseEnter={() => setActivo(i)}
                className={`px-3 py-2 cursor-pointer ${i === activo ? 'bg-brand-50 text-brand-700' : 'hover:bg-slate-50'}`}
              >
                <span className="font-mono">{codigo}</span>
                {detalle}
                {s.activo === false && <span className="ml-1 text-xs text-amber-700"> (inactivo)</span>}
                {precio > 0 && <span className="ml-1 text-xs text-slate-400"> ${precio.toLocaleString('es-AR')}</span>}
              </li>
            );
          })}
        </ul>
      )}
      {mostrarProveedor && <ProveedorElegido proveedor={comprometida?.proveedor} />}
      {ambiguo && (
        <p id={idAviso} role="alert" className="mt-0.5 text-xs text-red-600">Este código existe en {ambiguo}: elegí cuál</p>
      )}
    </div>
  );
}
